import { useMemo, type ReactNode } from "react";
import classNames from "classnames";
import { useInertRef } from "@hooks/useInertRef";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import styles from "./HideableToolbar.module.scss";

interface HideableToolbarProps {
    isHidden: boolean;
    children: ReactNode;
}

const HideableToolbar = ({ isHidden, children }: HideableToolbarProps) => {
    const inertRef = useInertRef(isHidden);
    return (
        <div
            ref={inertRef}
            className={classNames(styles.toolbar, {
                [styles.hidden]: isHidden,
            })}
        >
            {children}
        </div>
    );
};

/**
 * Portals the list's view toolbar into the sub-navigation slot, fading it out
 * while a task is open. It keeps its height, so the list under it never moves
 * (and never re-measures) while the open task covers both.
 */
export const useHideableSubNavigationToolbar = ({
    id,
    content,
    isHidden,
}: {
    id: string;
    content: ReactNode;
    isHidden: boolean;
}) => {
    const toolbar = useMemo(
        () => <HideableToolbar isHidden={isHidden}>{content}</HideableToolbar>,
        [content, isHidden],
    );
    useSubNavigationPortalContent(id, toolbar);
};
